import React from 'react';
import BsModal from '../Common/BsModal';
import { Skeleton } from '../../util/skeleton/Skeleton';
import { formatDate } from '../../util/araknerdlib';

const DeviceSkeleton = () => (
  <div className="border rounded-6 p-3 mb-2">
    <div className="d-flex justify-content-between mb-2">
      <Skeleton w="40%" h={14} />
      <Skeleton w="25%" h={12} />
    </div>
    <Skeleton w="60%" h={12} />
    <div className="d-flex mt-2">
      <Skeleton w="30%" h={10} />
      <Skeleton w="30%" h={10} />
    </div>
  </div>
);

const EmptyState = ({ macAddress }) => (
  <div className="text-center text-muted py-4">
    <i className="fe fe-wifi-off d-block mb-2 tx-26" />
    <div className="tx-13 mb-1">
      No devices found for
      {macAddress ? (
        <>
          {' '}
          <code>{macAddress}</code>
        </>
      ) : (
        ' this MAC address'
      )}
      .
    </div>
    <small className="tx-11">
      Try confirming the MAC address from your router logs and search again.
    </small>
  </div>
);

const renderSpeed = (bps) => {
  if (!bps || bps <= 0) return '0 Mbps';
  const mbps = bps / (1024 * 1024);
  if (mbps < 1) {
    const kbps = bps / 1024;
    return `${kbps.toFixed(1)} Kbps`;
  }
  return `${mbps.toFixed(2)} Mbps`;
};

const renderStatusBadge = (device) => {
  const status = (device.router_status_message || '').toLowerCase();
  const isConnected = status === 'connected';

  const baseClass = 'badge rounded-pill';
  const cls = isConnected
    ? `${baseClass} bg-success-light text-success`
    : `${baseClass} bg-secondary-light text-muted`;

  return (
    <span className={cls}>
      {isConnected ? 'Connected' : 'Disconnected'}
    </span>
  );
};

const renderDeviceCard = (device, onSelectDevice) => {
  const info = device.device_info || {};

  // Fallback parse from device_name string if needed
  let manufacturer = info.manufacturer;
  let os = info.os;
  let nickname = info.nickname;

  if (!manufacturer || !os || !nickname) {
    const parts = (device.device_name || '').split(';');
    manufacturer = manufacturer || parts[0] || 'Unknown Manufacturer';
    os = os || parts[1] || 'Unknown OS';
    nickname = nickname || parts[2] || 'Unknown Device';
  }

  const displayNickname =
    nickname && nickname !== 'Unknown Device' ? nickname : 'Unnamed Device';

  const displayManufacturer =
    manufacturer && manufacturer !== 'Unknown Manufacturer'
      ? manufacturer
      : null;

  const displayOs =
    os && os !== 'Unknown OS'
      ? os
      : null;

  const lastSeen = device.heart_beat || device.updated_at;
  const createdAt = device.created_at
    ? formatDate(device.created_at)
    : 'Unknown';

  const description =
    device.router_status_description ||
    'No additional status description.';

  const isClickable = typeof onSelectDevice === 'function';

  return (
    <div
      key={device.id}
      className={`border rounded-6 p-3 mb-2 bg-light ${
        isClickable ? 'link' : ''
      }`}
      onClick={() => isClickable && onSelectDevice(device.device_token)}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <div className="d-flex align-items-center mb-1">
            <h6 className="mb-0 me-2">{displayNickname}</h6>
            {renderStatusBadge(device)}
          </div>
          <small className="text-muted">
            {displayManufacturer && <span>{displayManufacturer}</span>}
            {displayManufacturer && displayOs && <span> · </span>}
            {displayOs && <span>{displayOs}</span>}
            {!displayManufacturer && !displayOs && (
              <span>Unknown device type</span>
            )}
          </small>
        </div>
        <div className="text-end">
          <small className="d-block text-muted tx-11">Last heartbeat</small>
          <small className="tx-11">
            {lastSeen ? formatDate(lastSeen) : 'Never'}
          </small>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center tx-12 mb-2">
        <span className="me-3">
          <i className="fe fe-hash me-1 tx-12" />
          <strong>MAC:</strong> {device.mac_address || 'Unknown'}
        </span>
        <span className="me-3">
          <i className="fe fe-map-pin me-1 tx-12" />
          <strong>IP:</strong> {device.last_ip || 'Unknown'}
        </span>
        <span className="me-3">
          <i className="fe fe-clock me-1 tx-12" />
          <strong>Created:</strong> {createdAt}
        </span>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between tx-11 mb-2">
        <div className="d-flex flex-wrap align-items-center">
          <span className="me-3">
            <i className="fe fe-arrow-up-right me-1 tx-12" />
            <strong>Up:</strong> {renderSpeed(device.upload_speed_bps)}
          </span>
          <span className="me-3">
            <i className="fe fe-arrow-down-right me-1 tx-12" />
            <strong>Down:</strong> {renderSpeed(device.download_speed_bps)}
          </span>
        </div>

        <div className="text-end">
          {device.last_voucher_code && (
            <span className="badge bg-primary-light me-1">
              Voucher: <strong>{device.last_voucher_code}</strong>
            </span>
          )}
          {device.device_token && (
            <span className="badge bg-info-light">
              Token:{' '}
              <strong>
                {device.device_token.length > 16
                  ? device.device_token.slice(0, 16) + '…'
                  : device.device_token}
              </strong>
            </span>
          )}
        </div>
      </div>

      <small className="text-muted d-block">
        {description}
      </small>
    </div>
  );
};

const SearchDeviceModal = ({
  show,
  onClose,
  macAddress,     // string: MAC the user searched for
  results,        // array | [] | null
  loading = false,
  onSelectDevice, // optional callback(device)
}) => {
  const hasResults = Array.isArray(results) && results.length > 0;

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="Device Search Results"
      size='modal-lg'
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
        </>
      }
    >
      <div className="text-dark">
        <p className="mb-2 tx-13">
          Showing results for MAC address{' '}
          {macAddress ? <code>{macAddress}</code> : '…'}
        </p>

        {/* Loading state */}
        {loading && (
          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            <DeviceSkeleton />
            <DeviceSkeleton />
          </div>
        )}

        {/* Results / empty state */}
        {!loading && (
          <>
            {!results && (
              <div className="text-muted tx-12 py-3">
                Waiting for results&hellip;
              </div>
            )}

            {results === '404' && (
              <EmptyState macAddress={macAddress} />
            )}

            {hasResults && (
              <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {results.map((device) =>
                  renderDeviceCard(device, onSelectDevice)
                )}
              </div>
            )}
          </>
        )}
      </div>
    </BsModal>
  );
};

export default SearchDeviceModal;
